import type { ComponentPropsWithoutRef } from 'react';
import { useEffect, useRef, useState } from 'react';
import { BarStackHorizontal } from '@visx/shape';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { useTheme } from '@simplywallst/ui-core';
import useWindowSize from '@/hooks/useWindowSize';
import Label from '../Label';
import YAxis from '../YAxis';
import XAxis from '../XAxis';
import { PatternLines } from '@visx/pattern';
import { Key } from '@components/TimeSeries';
import { Legend, Dot } from './styled';
import { Column } from '@simplywallst/ui-core';
import Tooltip from '@components/Tooltip';
import { useServerEnv, Size } from '@/hooks/useServerEnv';
import { TooltipTitle } from './TooltipTitle';
enum Labels {
  individualSharesBought = 'bought by individuals',
  companySharesBought = 'bought by companies',
  individualSharesSold = 'sold by individuals',
  companySharesSold = 'sold by companies',
}

const series = ['0–3', '3–6', '6–9', '9–12'];
const buyKeys = ['individualSharesBought', 'companySharesBought'];
const sellKeys = ['individualSharesSold', 'companySharesSold'];
const patternSellId = 'insider-trading-sell-lines';
const patternBuyId = 'insider-trading-buy-lines';

export interface Entity {
  companySharesBought: number;
  companySharesSold: number;
  companyBuyCount: number;
  companySellCount: number;
  individualSharesBought: number;
  individualSharesSold: number;
  individualBuyCount: number;
  individualSellCount: number;
  totalBought: number;
  totalSold: number;
  totalBoughtValue: string;
  totalSoldValue: string;
}

interface Props {
  data: Entity[];
  labels?: {
    individualSharesBought: string;
    companySharesBought: string;
    individualSharesSold: string;
    companySharesSold: string;
    xAxisLabel1?: string;
    xAxisLabel2?: string;
    yAxisLabelRender?: ComponentPropsWithoutRef<typeof YAxis>['labelRender'];
    chartLabel1?: (shares: number) => string;
    chartLabel2?: (value: string) => string;
  };
}

const InsiderTrading = ({
  data,
  labels = {
    individualSharesBought: 'Bought by individuals',
    companySharesBought: 'Bought by companies',
    individualSharesSold: 'Sold by individuals',
    companySharesSold: 'Sold by companies',
    xAxisLabel1: undefined,
    xAxisLabel2: undefined,
    yAxisLabelRender: undefined,
    chartLabel1: undefined,
    chartLabel2: undefined,
  },
}: Props) => {
  const theme = useTheme();
  const { width: screenWidth = 0 } = useWindowSize();
  const SVGRef = useRef<HTMLDivElement>(null);
  const serverEnv = useServerEnv();
  const breakPoints = {
    [Size.SM]: 379,
    [Size.LG]: 821,
  };
  const [chartWidth, setChartWidth] = useState(
    serverEnv.isServer ? breakPoints[serverEnv.size] : 100
  );
  const [yScalar, setYScalar] = useState<number>(theme.x9spacePx);

  useEffect(() => {
    const { current } = SVGRef;
    if (current) {
      const width = current.getBoundingClientRect().width;
      if (width > 0) {
        setChartWidth(width);
      }
      if (screenWidth <= theme.mdMaxRaw && yScalar !== theme.x12spacePx) {
        setYScalar(theme.x12spacePx);
      } else if (screenWidth > theme.mdMaxRaw && yScalar !== theme.x9spacePx) {
        setYScalar(theme.x9spacePx);
      }
    }
  }, [
    chartWidth,
    screenWidth,
    theme.mdMaxRaw,
    theme.x12spacePx,
    theme.x9spacePx,
    yScalar,
  ]);

  const yPadding = theme.x5spacePx;
  const xPadding = theme.x7spacePx;
  const barStackWidth = chartWidth - xPadding;
  const max = Math.max(
    ...data.map((n) => Math.max(n.totalBought, n.totalSold))
  );

  const yScale = scaleBand({
    domain: series,
    range: [0, yScalar * series.length],
  });

  const xScale = scaleLinear({
    domain: [0, max],
    range: [0, 1],
  });

  const buyColor = scaleOrdinal({
    domain: buyKeys,
    range: [theme.color.good, `url('#${patternBuyId}')`],
  });

  const sellColor = scaleOrdinal({
    domain: sellKeys,
    range: [theme.color.warning, `url('#${patternSellId}')`],
  });

  const getTranslatedLabel = (label: Labels) => {
    switch (label) {
      case Labels.individualSharesBought:
        return labels.individualSharesBought;
      case Labels.companySharesBought:
        return labels.companySharesBought;
      case Labels.individualSharesSold:
        return labels.individualSharesSold;
      case Labels.companySharesSold:
        return labels.companySharesSold;
    }
  };

  return (
    <div ref={SVGRef}>
      <svg
        width="100%"
        height={yScale.range()[1] + yPadding}
        shapeRendering="crispEdges"
      >
        <XAxis
          x={xPadding}
          width={barStackWidth}
          labels={
            typeof labels.xAxisLabel1 === 'string' &&
            typeof labels.xAxisLabel2 === 'string'
              ? {
                  first: labels.xAxisLabel1,
                  second: labels.xAxisLabel2,
                }
              : undefined
          }
        />
        <YAxis
          y={yPadding}
          yScale={yScale}
          series={series}
          labelRender={labels.yAxisLabelRender}
        />
        <svg
          x={xPadding}
          y={yPadding}
          width={barStackWidth / 2}
          height={yScale.range()[1]}
          shapeRendering="crispEdges"
        >
          <PatternLines
            id={patternSellId}
            height={5}
            width={5}
            stroke={theme.color.warning}
            strokeWidth={1}
            orientation={['diagonal']}
          />
          <BarStackHorizontal
            data={data}
            keys={sellKeys}
            y={() => '0'}
            xScale={xScale}
            yScale={yScale}
            color={sellColor}
          >
            {(barStacks) => {
              return barStacks.map((barStack) => {
                return barStack.bars.map(
                  ({ x, key, index, width, height, color, bar: { data } }) => (
                    <svg
                      y={index * height}
                      x={theme.x1spacePx}
                      width={barStackWidth / 2 - 16}
                      height={height}
                      key={`insider-trading--${barStack.key}-${index}`}
                      data-cy-id={`chart-insider-trading-${key}-${series[index]}`}
                    >
                      {key === sellKeys[1] && (
                        <Label
                          y={theme.x4spacePx}
                          type="sold"
                          shares={data.totalSold}
                          value={data.totalSoldValue}
                          height={height - theme.x3spacePx}
                          width={barStackWidth / 2 - 16}
                          individuals={data.individualSellCount}
                          companies={data.companySellCount}
                          stack={screenWidth <= theme.mdMaxRaw}
                          labels={
                            typeof labels.chartLabel1 !== 'undefined' &&
                            typeof labels.chartLabel2 !== 'undefined'
                              ? {
                                  label1: labels.chartLabel1(data.totalSold),
                                  label2: labels.chartLabel2(
                                    data.totalSoldValue
                                  ),
                                }
                              : undefined
                          }
                        />
                      )}
                      <rect
                        x={`${100 - x * 100 - width * 100}%`}
                        y={theme.x1spacePx}
                        width={`${width * 100}%`}
                        height={theme.x2spacePx}
                        fill={color}
                      />
                      {data.individualSellCount === 0 &&
                      data.companySellCount === 0 ? null : (
                        <Tooltip
                          title={
                            <TooltipTitle
                              type="sold"
                              total={data.totalSold}
                              individualCount={data.individualSellCount}
                              companyCount={data.companySellCount}
                              totalValue={data.totalSoldValue}
                            />
                          }
                          placement="top"
                          offsetY={-16}
                        >
                          <rect
                            x="0"
                            y="0"
                            width="100%"
                            height="100%"
                            fill="transparent"
                          />
                        </Tooltip>
                      )}
                    </svg>
                  )
                );
              });
            }}
          </BarStackHorizontal>
        </svg>
        <svg
          x={xPadding + barStackWidth / 2}
          y={yPadding}
          width={barStackWidth / 2}
          height={yScale.range()[1]}
          shapeRendering="crispEdges"
        >
          <PatternLines
            id={patternBuyId}
            height={5}
            width={5}
            stroke={theme.color.good}
            strokeWidth={1}
            orientation={['diagonal']}
          />
          <BarStackHorizontal
            data={data}
            keys={buyKeys}
            y={() => '0'}
            xScale={xScale}
            yScale={yScale}
            color={buyColor}
          >
            {(barStacks) => {
              return barStacks.map((barStack) =>
                barStack.bars.map(
                  ({ x, key, index, width, height, color, bar: { data } }) => (
                    <svg
                      y={index * height}
                      x={theme.x1spacePx}
                      width={barStackWidth / 2 - 16}
                      height={height}
                      key={`insider-trading--${barStack.key}-${index}`}
                      data-cy-id={`chart-insider-trading-${key}-${series[index]}`}
                    >
                      {key === buyKeys[1] && (
                        <Label
                          y={theme.x4spacePx}
                          type="bought"
                          shares={data.totalBought}
                          value={data.totalBoughtValue}
                          height={height - theme.x3spacePx}
                          width={barStackWidth / 2 - 16}
                          individuals={data.individualBuyCount}
                          companies={data.companyBuyCount}
                          stack={screenWidth <= theme.mdMaxRaw}
                          labels={
                            typeof labels.chartLabel1 !== 'undefined' &&
                            typeof labels.chartLabel2 !== 'undefined'
                              ? {
                                  label1: labels.chartLabel1(data.totalBought),
                                  label2: labels.chartLabel2(
                                    data.totalBoughtValue
                                  ),
                                }
                              : undefined
                          }
                        />
                      )}
                      <rect
                        x={`${x * 100}%`}
                        y={theme.x1spacePx}
                        width={`${width * 100}%`}
                        height={theme.x2spacePx}
                        fill={color}
                      />
                      {data.individualBuyCount === 0 &&
                      data.companyBuyCount === 0 ? null : (
                        <Tooltip
                          title={
                            <TooltipTitle
                              type="bought"
                              total={data.totalBought}
                              individualCount={data.individualBuyCount}
                              companyCount={data.companyBuyCount}
                              totalValue={data.totalBoughtValue}
                            />
                          }
                          placement="top"
                          offsetY={-16}
                        >
                          <rect
                            x="0"
                            y="0"
                            width="100%"
                            height="100%"
                            fill="transparent"
                          />
                        </Tooltip>
                      )}
                    </svg>
                  )
                )
              );
            }}
          </BarStackHorizontal>
        </svg>
        <rect
          x="0"
          y={yScale.range()[1] + yPadding - 1}
          width="100%"
          height="1"
          fill={theme.color.white}
          fillOpacity="0.5"
        />
      </svg>
      <Legend>
        <Column col={6}>
          {sellKeys.map((n) => (
            <Key
              key={`insider-trading-key-${n}`}
              color={n !== sellKeys[1] ? theme.color.warning : undefined}
            >
              {n !== sellKeys[0] && (
                <Dot width="8" height="8">
                  <circle
                    cx="4"
                    cy="4"
                    r="4"
                    fill={theme.color.warning}
                    fillOpacity="0.3"
                  />
                  <circle cx="4" cy="4" r="4" fill={sellColor(n)} />
                </Dot>
              )}
              {getTranslatedLabel(Labels[n])}
            </Key>
          ))}
        </Column>
        <Column col={6}>
          {buyKeys.map((n) => (
            <Key
              key={`insider-trading-key-${n}`}
              color={n !== buyKeys[1] ? theme.color.good : undefined}
            >
              {n !== buyKeys[0] && (
                <Dot width="8" height="8">
                  <circle
                    cx="4"
                    cy="4"
                    r="4"
                    fill={theme.color.good}
                    fillOpacity="0.3"
                  />
                  <circle cx="4" cy="4" r="4" fill={buyColor(n)} />
                </Dot>
              )}
              {getTranslatedLabel(Labels[n])}
            </Key>
          ))}
        </Column>
      </Legend>
    </div>
  );
};

export default InsiderTrading;
